import { 
  Claim, 
  ClaimStatus, 
  EncounterType, 
  PayerName, 
  DenialReason,
  AgentAction,
  KPIData,
  CashVelocityData,
  DenialPreventionData,
  PayerDrift,
  Conversation,
  ConversationMessage
} from './types';

const PAYERS: PayerName[] = ['Medicare', 'BCBS', 'Aetna', 'UnitedHealthcare', 'Cigna', 'Humana'];
const ENCOUNTER_TYPES: EncounterType[] = ['Ortho Surgery', 'Physical Therapy', 'Imaging', 'Office Visit', 'Injection'];

const CPT_CODES: Record<EncounterType, string[]> = {
  'Ortho Surgery': ['27447', '27130', '29881', '29827', '27446'],
  'Physical Therapy': ['97110', '97140', '97530', '97542', '97112'],
  'Imaging': ['73721', '73221', '72148', '73502', '73560'],
  'Office Visit': ['99213', '99214', '99215', '99203', '99204'],
  'Injection': ['20610', '64450', '64451', '64493', '27096'],
};

const ICD10_CODES: Record<EncounterType, string[]> = {
  'Ortho Surgery': ['M17.11', 'M16.11', 'S83.241A', 'M75.121', 'M79.3'],
  'Physical Therapy': ['M54.5', 'M25.561', 'S83.241A', 'M75.121', 'M79.3'],
  'Imaging': ['M25.561', 'M54.5', 'M17.11', 'M16.11', 'M79.3'],
  'Office Visit': ['M17.11', 'M25.561', 'M54.5', 'M79.3', 'M75.121'],
  'Injection': ['M17.11', 'M54.5', 'M79.3', 'M25.561', 'M75.121'],
};

const MODIFIERS = ['-RT', '-LT', '-59', '-25', '-76', '-XE', '-XS'];

const DENIAL_REASONS: DenialReason[] = ['auth_missing', 'coding_mismatch', 'eligibility', 'bundling', 'timely_filing', 'medical_necessity'];

const INITIALS = ['JD', 'MK', 'RW', 'SL', 'AP', 'TC', 'BH', 'KM', 'LS', 'NP', 'DG', 'EF', 'GH', 'VR', 'WS'];

function randomFromArray<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateClaimId(): string {
  return `CLM-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
}

function generateAgentActions(status: ClaimStatus, encounterType: EncounterType): AgentAction[] {
  const actions: AgentAction[] = [];
  const now = new Date();
  
  const statusOrder: ClaimStatus[] = ['note_signed', 'coding', 'denial_check', 'auto_fix', 'submitted', 'pending', 'paid'];
  const currentIndex = statusOrder.indexOf(status);
  
  if (currentIndex >= 0) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex + 1)),
      agent: 'coding',
      action: 'Note analyzed',
      detail: 'Extracted procedure details and diagnosis codes from clinical documentation',
      confidence: 0.98,
    });
  }
  
  if (currentIndex >= 1) {
    const cpt = randomFromArray(CPT_CODES[encounterType]);
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * currentIndex),
      agent: 'coding',
      action: 'CPT/ICD-10 assigned',
      detail: `Assigned ${cpt} with modifier based on laterality and procedure complexity`,
      confidence: 0.95,
    });
  }
  
  if (currentIndex >= 2) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex - 1)),
      agent: 'denial_prevention',
      action: 'Payer rules simulated',
      detail: 'Ran claim through predictive denial engine. Identified 2 risk factors.',
      confidence: 0.92,
    });
  }
  
  if (currentIndex >= 3 && Math.random() > 0.5) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex - 2)),
      agent: 'auto_fix',
      action: 'Auto-correction applied',
      detail: 'Added modifier -59 to prevent bundling denial. Updated diagnosis sequence.',
      confidence: 0.89,
    });
  }
  
  if (currentIndex >= 4) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex - 3)),
      agent: 'submission',
      action: 'Claim submitted',
      detail: 'Submitted via EDI 837P. Confirmation received.',
      confidence: 1.0,
    });
  }
  
  return actions;
}

export function generateClaim(overrides?: Partial<Claim>): Claim {
  const encounterType = randomFromArray(ENCOUNTER_TYPES);
  const payer = randomFromArray(PAYERS);
  const status = randomFromArray(['note_signed', 'coding', 'denial_check', 'submitted', 'pending', 'paid', 'denied'] as ClaimStatus[]);
  
  const expectedAmount = encounterType === 'Ortho Surgery' 
    ? randomBetween(15000, 45000)
    : encounterType === 'Physical Therapy'
    ? randomBetween(150, 450)
    : encounterType === 'Imaging'
    ? randomBetween(500, 2500)
    : randomBetween(200, 800);
  
  const allowedAmount = Math.floor(expectedAmount * (0.6 + Math.random() * 0.3));
  const patientResponsibility = Math.floor(allowedAmount * (0.1 + Math.random() * 0.2));
  
  const denialRisk = status === 'denied' ? randomBetween(70, 95) : randomBetween(2, 35);
  
  return {
    id: generateClaimId(),
    patientInitials: randomFromArray(INITIALS),
    encounterType,
    encounterDate: new Date(Date.now() - randomBetween(1, 14) * 86400000),
    payer,
    cptCodes: [randomFromArray(CPT_CODES[encounterType]), ...(Math.random() > 0.6 ? [randomFromArray(CPT_CODES[encounterType])] : [])],
    icd10Codes: [randomFromArray(ICD10_CODES[encounterType]), ...(Math.random() > 0.5 ? [randomFromArray(ICD10_CODES[encounterType])] : [])],
    modifiers: Math.random() > 0.3 ? [randomFromArray(MODIFIERS)] : [],
    expectedAmount,
    allowedAmount,
    patientResponsibility,
    denialRisk,
    denialReasons: denialRisk > 20 ? [randomFromArray(DENIAL_REASONS)] : [],
    status,
    agentActions: generateAgentActions(status, encounterType),
    submittedAt: ['submitted', 'pending', 'paid', 'denied'].includes(status) ? new Date(Date.now() - randomBetween(1, 7) * 86400000) : undefined,
    paidAt: status === 'paid' ? new Date(Date.now() - randomBetween(0, 3) * 86400000) : undefined,
    autoFixesApplied: Math.random() > 0.5 ? randomBetween(1, 3) : 0,
    facility: 'Primary Orthopaedics',
    ...overrides,
  };
}

export function generateClaims(count: number): Claim[] {
  return Array.from({ length: count }, () => generateClaim());
}

export function generateKPIData(): KPIData {
  return {
    collectionsToday: randomBetween(125000, 185000),
    collectionsDelta: randomBetween(-5, 15),
    claimsSubmitted24h: randomBetween(145, 220),
    claimsDelta: randomBetween(-8, 12),
    denialsPrevented24h: randomBetween(18, 35),
    denialsPreventedDelta: randomBetween(5, 25),
    avgDaysToCash: randomBetween(8, 12),
    daysToCashDelta: randomBetween(-3, -1),
    moneyAtRisk: randomBetween(45000, 85000),
    moneyAtRiskDelta: randomBetween(-12, -2),
  };
}

export function generateCashVelocityData(): CashVelocityData[] {
  const data: CashVelocityData[] = [];
  const now = new Date();
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 86400000);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      daysToCash: 8 + Math.sin(i / 5) * 3 + (Math.random() - 0.5) * 2,
      claimVolume: randomBetween(140, 200),
      targetMin: 7,
      targetMax: 10,
    });
  }
  
  return data;
}

export function generateDenialPreventionData(): DenialPreventionData[] {
  const data: DenialPreventionData[] = [];
  const now = new Date();
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 86400000);
    const prevented = randomBetween(15, 35);
    const occurred = randomBetween(2, 8);
    
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      prevented,
      occurred,
      authMissing: Math.floor(prevented * 0.3),
      codingMismatch: Math.floor(prevented * 0.25),
      eligibility: Math.floor(prevented * 0.2),
      bundling: Math.floor(prevented * 0.25),
    });
  }
  
  return data;
}

export function generatePayerDriftData(): PayerDrift[] {
  return [
    {
      payer: 'Medicare',
      driftScore: 23,
      topChange: 'New bundling rules for 27447',
      impact: -12400,
      suggestedFix: 'Add modifier -59 for bilateral procedures',
    },
    {
      payer: 'BCBS',
      driftScore: 18,
      topChange: 'Auth requirements for imaging',
      impact: -8200,
      suggestedFix: 'Auto-request auth for MRI orders',
    },
    {
      payer: 'UnitedHealthcare',
      driftScore: 15,
      topChange: 'PT visit limits reduced',
      impact: -5600,
      suggestedFix: 'Request extension after visit 12',
    },
    {
      payer: 'Aetna',
      driftScore: 8,
      topChange: 'Payment timing slower',
      impact: -2100,
      suggestedFix: 'Monitor and escalate after 21 days',
    },
  ];
}

export function generateConversations(): Conversation[] {
  return [
    {
      id: 'conv-001',
      patientInitials: 'MK',
      channel: 'sms',
      intent: 'eob_explanation',
      sentiment: 0.6,
      status: 'active',
      suggestedResolution: 'Explain insurance adjustment vs patient responsibility',
      aiMode: true,
      createdAt: new Date(Date.now() - 1800000),
      balanceDue: 847.50,
      messages: [
        {
          id: 'msg-001',
          sender: 'patient',
          content: 'I got a bill for $847 but my insurance should cover this. Can you help?',
          timestamp: new Date(Date.now() - 1800000),
        },
        {
          id: 'msg-002',
          sender: 'agent',
          content: 'I\'d be happy to help explain your bill. Your insurance (BCBS) covered $2,340 of the total $3,187.50 charge. The $847.50 is your responsibility based on your plan\'s deductible and coinsurance. Would you like me to break this down further?',
          timestamp: new Date(Date.now() - 1700000),
        },
      ],
    },
    {
      id: 'conv-002',
      patientInitials: 'JD',
      channel: 'voice',
      intent: 'payment_plan',
      sentiment: 0.4,
      status: 'active',
      suggestedResolution: 'Offer 6-month payment plan at $234/month',
      aiMode: true,
      createdAt: new Date(Date.now() - 3600000),
      balanceDue: 1404.00,
      messages: [
        {
          id: 'msg-003',
          sender: 'patient',
          content: '[Voice transcript] I can\'t afford to pay this all at once. The surgery bill is too high.',
          timestamp: new Date(Date.now() - 3600000),
        },
        {
          id: 'msg-004',
          sender: 'agent',
          content: 'I understand medical expenses can be challenging. Based on your balance of $1,404, I can offer you a 6-month payment plan at $234/month with no interest. Would that work for you?',
          timestamp: new Date(Date.now() - 3500000),
        },
      ],
    },
    {
      id: 'conv-003',
      patientInitials: 'RW',
      channel: 'web',
      intent: 'dispute',
      sentiment: 0.2,
      status: 'escalated',
      suggestedResolution: 'Review coding for duplicate charge, escalate to billing manager',
      aiMode: false,
      createdAt: new Date(Date.now() - 7200000),
      balanceDue: 2150.00,
      messages: [
        {
          id: 'msg-005',
          sender: 'patient',
          content: 'I was charged twice for the same X-ray. This is unacceptable and I want it fixed immediately.',
          timestamp: new Date(Date.now() - 7200000),
        },
        {
          id: 'msg-006',
          sender: 'agent',
          content: 'I apologize for the frustration. I\'m reviewing your account now and can see there may be a duplicate charge. I\'m escalating this to our billing team for immediate review. You\'ll hear back within 24 hours.',
          timestamp: new Date(Date.now() - 7100000),
        },
      ],
    },
  ];
}

export const BEACON_METRICS = {
  before: {
    billingTouchesPerClaim: 12,
    denialRate: 14,
    daysToCash: 21,
    netCollectionsChange: 0,
  },
  after: {
    billingTouchesPerClaim: 1,
    denialRate: 3,
    daysToCash: 9,
    netCollectionsChange: 18,
  },
};

export const DR_LARKIN_ENDORSEMENT = `"LINK transformed our revenue cycle. Our billers went from chasing denials to strategic oversight. The AI doesn't just code—it thinks like our best biller, but faster and more consistent."`;
